import Vue from "vue";
import Router, { isNavigationFailure, NavigationFailureType } from "vue-router";
import store from "./store"
import {callAPI} from "./mixins/api"
import {redirectToLogin} from "./oauth"
import { FEATURE_BROWSE_REPOSITORIES, FEATURE_MY_DOCUMENT_RETENTION } from "./util/featureFlags";

Vue.use(Router);

function isExpectedNavigationFailure(error) {
  return isNavigationFailure(error, NavigationFailureType.duplicated)
    || isNavigationFailure(error, NavigationFailureType.cancelled);
}

function ignoreExpectedNavigationFailures(routerMethod) {
  return function navigate(location, onResolve, onReject) {
    if (onResolve || onReject) {
      return routerMethod.call(this, location, onResolve, onReject);
    }
    return routerMethod.call(this, location).catch((error) => {
      if (isExpectedNavigationFailure(error)) {
        return error;
      }
      return Promise.reject(error);
    });
  };
}

Router.prototype.push = ignoreExpectedNavigationFailures(Router.prototype.push);
Router.prototype.replace = ignoreExpectedNavigationFailures(Router.prototype.replace);

let router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      beforeEnter: (to, from, next) => {
        next({ name: FEATURE_BROWSE_REPOSITORIES ? "browse-repositories" : "repositories" });
      },
    },
    {
      path: "/repositories",
      name: "repositories",
      component: () =>
        import(
          /* webpackChunkName: "home" */ "./components/pages/repositories/Page.vue"
        ),
    },
    {
      path: "/all",
      name: "all",
      component: () =>
        import(
          /* webpackChunkName: "home" */ "./components/pages/all_items/Page.vue"
        ),
    },
    {
      path: "/shared",
      name: "shared",
      component: () =>
        import(
          /* webpackChunkName: "home" */ "./components/pages/shared/Page.vue"
        ),
    },
    {
      path: "/browse-repositories",
      name: "browse-repositories",
      beforeEnter: (to, from, next) => {
        if (!FEATURE_BROWSE_REPOSITORIES) {
          next({ name: "repositories" });
          return;
        }
        next();
      },
      component: () =>
        import(
          /* webpackChunkName: "home" */ "./components/pages/browse-repositories/Page.vue"
        ),
    },
    {
      path: "/users",
      name: "users",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/users/Page.vue"
        ),
    },
    {
      path: "/clients",
      name: "clients",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/clients/Page.vue"
        ),
      meta: { requiresAdmin: true },
    },
    {
      path: "/teams",
      name: "teams",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/teams/Page.vue"
        ),
    },
    {
      path: "/personal-storage",
      name: "personal-storage",
      beforeEnter: async (to, from, next) => {
        await store().dispatch("setBreadcrumbs", []);
        next({
          path:
            "/browse/" +
            store().state.auth.userData.personalStorageFolder +
            "/personal-storage",
        });
      },
    },
    {
      path: "/reds-search",
      name: "reds-search",
      component: () =>
        import(
          /* webpackChunkName: "reds" */ "./components/pages/reds-search/Page"
        ),
    },
    {
      path: "/reds-results",
      name: "reds-results",
      beforeEnter: (to, from, next) => {
        next({ name: "reds-search", query: to.query });
      },
    },
    {
      path: "/view/:id",
      name: "view-item",
      beforeEnter: async (to) => {
        await store().dispatch("initPage");
        let res = await callAPI(store(), "item/view/" + to.params.id);
        window.location = res.url;
      },
    },
    {
      path: "/repository/by-name",
      name: "find-repository-by-name",
      beforeEnter: async (to, from, next) => {
        await store().dispatch("initPage");
        let res = await callAPI(store(), "item/repository/by-name/", {
          query: { name: to.query.name },
        });
        next({ path: "/browse/" + res.id });
      },
    },
    {
      path: "/browse/:id/upload",
      name: "upload",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/upload/RoutePage.vue"
        ),
    },
    {
      path: "/browse/:id/:flag?",
      name: "browse",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/browser/Page.vue"
        ),
    },
    {
      path: "/search",
      name: "search",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/search/Page.vue"
        ),
    },
    {
      path: "/logged-out",
      name: "logged-out",
      component: () =>
        import(
          /* webpackChunkName: "home" */ "./components/pages/logged-out/Page.vue"
        ),
    },
    {
      path: "/oauth/redirect*",
      name: "oauth-redirect",
      props: true,
      component: () => import("./components/OAuth.vue"),
    },
    {
      path: "/properties/:id",
      name: "properties",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/properties/Page.vue"
        ),
    },
    {
      path: "/folder-properties/:id?",
      name: "folder-properties",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/folder_properties/Page.vue"
        ),
    },
    {
      path: "/apdm/v4.0/by-id/item/:id/",
      name: "apdm-handler-item-id-1",
      component: () => undefined,
    },
    {
      path: "/v4.0/by-id/item/:id/",
      name: "apdm-handler-item-id-2",
      component: () => undefined,
    },
    {
      path: "/apdm/v4.0/by-ver/item/:id/:version",
      name: "apdm-handler-item-id-version-1",
      component: () => undefined,
    },
    {
      path: "/v4.0/by-ver/item/:id/:version",
      name: "apdm-handler-item-id-version-2",
      component: () => undefined,
    },
    {
      path: "/apdm/v4.0/by-id/item/:id/:label",
      name: "apdm-handler-item-id-label-1",
      component: () => undefined,
    },
    {
      path: "/v4.0/by-id/item/:id/:label",
      name: "apdm-handler-item-id-label-2",
      component: () => undefined,
    },
    {
      path: "/apdm/v4.0/by-id/:type/:id",
      name: "apdm-handler-type-id-1",
      component: () => undefined,
    },
    {
      path: "/v4.0/by-id/:type/:id",
      name: "apdm-handler-type-id-2",
      component: () => undefined,
    },
    {
      path: "/downloads",
      name: "downloads",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/downloads/Page.vue"
        ),
    },
    {
      path: "/uploads",
      name: "my-uploads",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/uploads/MyPage.vue"
        ),
    },
    {
      path: "/trash",
      name: "trash",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/trash/Page.vue"
        ),
    },
    {
      path: "/trash/jobs/:jobId/logs/:logId",
      name: "trash-job-details",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/trash/Details.vue"
        ),
    },
    {
      path: "/my-requests",
      redirect: { name: "archive-requests" },
    },
    {
      path: "/archive-requests",
      name: "archive-requests",
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/repository-requests/Page.vue"
        ),
    },
    {
      path: "/admin",
      name: "administration",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/administration-landing/Page.vue"
        ),
    },
    {
      path: "/admin/jobs",
      name: "jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/jobs-landing/Page.vue"
        ),
    },
    {
      path: "/admin/notification-routes",
      name: "notification-routes",
      meta: { requiresAdmin: true },
      component: () => import(/* webpackChunkName: "management" */ "./components/pages/notification-routes/Page.vue"),
    },
    {
      path: "/admin/notification-deliveries",
      name: "notification-deliveries",
      meta: { requiresAdmin: true },
      component: () => import(/* webpackChunkName: "management" */ "./components/pages/notification-deliveries/Page.vue"),
    },
    {
      path: "/admin/reporting",
      name: "reporting",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting-landing/Page.vue"
        ),
    },
    {
      path: "/admin/downloads",
      name: "admin-downloads",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/downloads/AdminPage.vue"
        ),
    },
    {
      path: "/admin/uploads",
      name: "admin-uploads",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/uploads/Page.vue"
        ),
    },
    {
      path: "/admin/deletion-jobs",
      name: "deletion-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/deletion-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/reindex-jobs",
      name: "reindex-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/reindex-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/item-property-patch-jobs",
      name: "folder-property-patch-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/folder-property-patch-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/folder-property-patch-jobs",
      redirect: { name: "folder-property-patch-jobs" },
    },
    {
      path: "/admin/storage-inventory-jobs",
      name: "storage-inventory-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/storage-inventory-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/storage-analytics-jobs",
      name: "storage-analytics-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/storage-analytics-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/storage-costing-jobs",
      name: "storage-costing-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/storage-costing-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/storage-pricing-jobs",
      name: "storage-pricing-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/storage-pricing-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/expiration-jobs",
      name: "expiration-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/expiration-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/volume-growth-jobs",
      name: "volume-growth-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/volume-growth-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/storage-cost-report-jobs",
      name: "storage-cost-report-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/storage-cost-report-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/3dx-ingest-jobs",
      name: "ingest-3dx-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/ingest-3dx-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/reds-log-ingest-jobs",
      name: "reds-log-ingest-jobs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/reds-log-ingest-jobs/Page.vue"
        ),
    },
    {
      path: "/admin/reporting/volume-growth",
      name: "reporting-volume-growth",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting/Page.vue"
        ),
    },
    {
      path: "/admin/reporting/storage-costs",
      name: "reporting-storage-costs",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting/StorageCostReportPage.vue"
        ),
    },
    {
      path: "/admin/reporting/storage-reconciliation",
      name: "reporting-storage-reconciliation",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting/StorageReconciliation.vue"
        ),
    },
    {
      path: "/admin/reporting/auditing",
      name: "reporting-auditing",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting/Auditing.vue"
        ),
    },
    {
      path: "/admin/reporting/storage-pricing",
      name: "reporting-storage-pricing",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/reporting/StoragePricingPage.vue"
        ),
    },
    {
      path: "/admin/maintenance",
      name: "maintenance-mode",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/maintenance/Page.vue"
        ),
    },
    {
      path: "/admin/system-banner",
      name: "system-banner",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/system-banner/Page.vue"
        ),
    },
    {
      path: "/admin/quarantine",
      name: "admin-quarantine",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/quarantine/Page.vue"
        ),
    },
    {
      path: "/admin/sensitive-name-rules",
      name: "sensitive-name-rules",
      meta: { requiresAdmin: true },
      component: () =>
        import(
          /* webpackChunkName: "management" */ "./components/pages/sensitive-name-rules/Page.vue"
        ),
    },
    {
      path: "/download/:id",
      name: "download-file",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/download/Page.vue"
        ),
    },
    {
      path: "/retention-schedules",
      name: "retention-schedules",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/retention/Page.vue"
        ),
    },
    {
      path: "/keywords",
      name: "keywords",
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/keywords/Page.vue"
        ),
    },
    {
      path: "/my-expired-items/:folderId?",
      name: "my-expired-items",
      beforeEnter: (to, from, next) => {
        if (!FEATURE_MY_DOCUMENT_RETENTION) {
          next({ name: "repositories" });
          return;
        }
        next();
      },
      component: () =>
        import(
          /* webpackChunkName: "browser" */ "./components/pages/expired/MyDocumentRetentionPage.vue"
        ),
    },
  ],
});

async function handleApdmUrl(apdmFunc) {
  let response = await apdmFunc();
  return response.path;
}

router.beforeEach(async (to, from, next) => {
  // Slight hack to handle old OAuth & UAA configuration, as the router doesn't properly handle
  // a route with a '#' in it if we're in history mode.
  if (to.fullPath.startsWith("/#/oauth/redirect")) {
    next(to.fullPath.substr(2));
    return;
  }

  await store().dispatch("initPage");
  const isLoggedOut = localStorage.getItem("loggedOut") === "true";
  if (!to.path.startsWith("/oauth/redirect") && !store().state.auth.token) {
    if (isLoggedOut) {
      if (to.name !== "logged-out") {
        next({ name: "logged-out" });
      } else {
        next();
      }
      return;
    }
    redirectToLogin({silent: false, originalRoute: to.fullPath})
  } else {
    const isAdmin = store().state.auth.userData?.isAdmin ?? false;
    if (to.matched.some((record) => record.meta && record.meta.requiresAdmin)) {
      if (!isAdmin) {
        next({ name: "repositories" });
        return;
      }
    }

    const maintenanceEnabled = store().state.maintenanceMode?.enabled ?? false;
    const isMaintenanceRoute = to.name === "maintenance-mode";
    if (maintenanceEnabled && !isAdmin && !isMaintenanceRoute && !to.path.startsWith("/oauth/redirect")) {
      next({ name: "maintenance-mode" });
      return;
    }
    if (!maintenanceEnabled && isMaintenanceRoute && !isAdmin) {
      next({ name: "repositories" });
      return;
    }

    let path = null;
    switch (to.name) {
      case "apdm-handler-item-id-1":
      case "apdm-handler-item-id-2":
        path = await handleApdmUrl(() => callAPI(store(), `apdm/v4.0/by-id/item/${to.params.id}/`, { method: "GET" }))
        break;
      case "apdm-handler-item-id-version-1":
      case "apdm-handler-item-id-version-2":
        path = await handleApdmUrl(() => callAPI(store(), `apdm/v4.0/by-ver/item/${to.params.id}/${to.params.version}`, { method: "GET" }))
        break;
      case "apdm-handler-item-id-label-1":
      case "apdm-handler-item-id-label-2":
        path = await handleApdmUrl(() => callAPI(store(), `apdm/v4.0/by-id/item/${to.params.id}/${to.params.label}`, { method: "GET" }))
        break;
      case "apdm-handler-type-id-1":
      case "apdm-handler-type-id-2":
        path = await handleApdmUrl(() => callAPI(store(), `apdm/v4.0/by-id/${to.params.type}/${to.params.id}`, { method: "GET" }))
        break;
    }

    if (path) {
      if (path.startsWith("https://")) {
        window.location = path;
      } else {
        next(path);
      }
    } else {
      next();
    }
  }
});

export default router;
